import { useMemo, useState } from 'react';
import {
  Box, FormControl, InputLabel, MenuItem, Paper, Select, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TableSortLabel, TextField, Typography,
} from '@mui/material';
import { safeDecode } from '../utils/decode';
import { percent } from './quotaGroups';
import { headCellSx, numeric, pairCellSx, pairRowSx, ProgressBar, tableContainerSx } from './QuotaTable';

const COLUMNS = [
  { id: 'name', label: 'Name', minWidth: 110 },
  { id: 'description', label: 'Description' },
  { id: 'responses', label: 'Completes', numeric: true },
  { id: 'limit', label: 'Limit', numeric: true },
  { id: 'progress', label: 'Progress', minWidth: 180 },
];

const sortValue = (quota, orderBy) => {
  if (orderBy === 'progress') return percent(quota) ?? -1;
  if (orderBy === 'name') return safeDecode(quota.name).toLowerCase();
  if (orderBy === 'description') return (quota.description || '').toLowerCase();
  return quota[orderBy];
};

/**
 * The partner page's table: name, description, completes, limit and progress, nothing else.
 * Partners' quotas come from one system only and carry no system, so there's nothing to tell
 * them apart by, and nothing here suggests there could be.
 */
const PartnerQuotaTable = ({ quotas }) => {
  const [searchField, setSearchField] = useState('name');
  const [searchQuery, setSearchQuery] = useState('');
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('name');

  const rows = useMemo(() => {
    const query = searchQuery.toLowerCase();
    const direction = order === 'desc' ? -1 : 1;
    return quotas
      .map((q, i) => ({ ...q, key: i }))
      .filter((q) => (searchField === 'name' ? safeDecode(q.name) : q.description || '').toLowerCase().includes(query))
      .sort((a, b) => {
        const va = sortValue(a, orderBy);
        const vb = sortValue(b, orderBy);
        if (typeof va === 'string' || typeof vb === 'string') {
          return direction * String(va).localeCompare(String(vb), undefined, { numeric: true });
        }
        return direction * (va - vb);
      });
  }, [quotas, searchField, searchQuery, orderBy, order]);

  if (quotas.length === 0) {
    return <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>No quotas to show</Typography>;
  }

  return (
    <>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField label="Search" size="small" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
        <FormControl sx={{ minWidth: 140 }} size="small">
          <InputLabel>Search By</InputLabel>
          <Select value={searchField} label="Search By" onChange={(e) => setSearchField(e.target.value)}>
            <MenuItem value="name">Name</MenuItem>
            <MenuItem value="description">Description</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <TableContainer component={Paper} elevation={0} sx={tableContainerSx}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {COLUMNS.map((col, i) => (
                <TableCell key={col.id} sx={headCellSx(col, i, COLUMNS.length)}>
                  <TableSortLabel active={orderBy === col.id} direction={orderBy === col.id ? order : 'asc'}
                    onClick={() => { setOrder(orderBy === col.id && order === 'asc' ? 'desc' : 'asc'); setOrderBy(col.id); }}>
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={COLUMNS.length} sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>No quotas match.</TableCell>
              </TableRow>
            )}
            {rows.map((quota, i) => {
              const cell = pairCellSx(true);
              return (
                <TableRow key={quota.key} sx={pairRowSx(i, i === rows.length - 1)}>
                  <TableCell sx={{ ...cell, fontWeight: 500 }}>{safeDecode(quota.name)}</TableCell>
                  <TableCell sx={{ ...cell, color: 'grey.700' }}>{quota.description || '—'}</TableCell>
                  <TableCell sx={{ ...cell, ...numeric }}>{quota.responses}</TableCell>
                  <TableCell sx={{ ...cell, ...numeric }}>{quota.limit}</TableCell>
                  <TableCell sx={cell}><ProgressBar quota={quota} /></TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default PartnerQuotaTable;
